import { OFQuestionDataType } from '../../../../generated/api/oFQuestionDataType.js';
import { OFQuestionType } from '../../../../generated/api/oFQuestionType.js';
import { type OpenFormAnswers } from '../../OpenFormAnswers.js';
import { OpenFormNotice } from '../../OpenFormComponents/OpenFormNotifications.js';
import { OpenFormQuestionBillingAccount } from './OpenFormQuestionBillingAccount.js';
import { OpenFormQuestionContact } from './OpenFormQuestionContact.js';
import { OpenFormQuestionDate } from './OpenFormQuestionDate.js';
import { OpenFormQuestionInput } from './OpenFormQuestionInput.js';
import { OpenFormQuestionListOfObjects } from './OpenFormQuestionListOfObjects.js';
import { OpenFormQuestionSelectGroup } from './OpenFormQuestionSelectGroup.js';
import { OpenFormQuestionSubscriptionReferences } from './OpenFormQuestionSubscriptionReferences.js';
import type { OpenFormQuestion } from '../../../../generated/api/openFormQuestion.js';

export const OpenFormQuestionType = ({
  question,
  answers,
  disabled,
}: {
  question: OpenFormQuestion;
  answers: OpenFormAnswers;
  disabled: boolean;
}) => {
  try {
    switch (question.type) {
      case OFQuestionType.LIST_OF_OBJECTS_MULTI_SELECT:
      case OFQuestionType.LIST_OF_OBJECTS_SINGLE_SELECT:
        return (
          <OpenFormQuestionListOfObjects
            disabled={disabled}
            required={question.isMandatory}
            label={question.label}
            infoText={question.description}
            name={question.guid}
            multiselect={question.type.endsWith('MULTI_SELECT')}
            columns={question.listColumns}
            visible={question.choices.filter(answers.isVisible)}
            context={answers.get(question)}
            availability={answers.getAvailability(question)}
          />
        );
      case OFQuestionType.CONTRACT_PERIOD:
      case OFQuestionType.MULTI_SELECT:
      case OFQuestionType.SINGLE_SELECT:
        return (
          <OpenFormQuestionSelectGroup
            disabled={disabled}
            required={question.isMandatory}
            label={question.label}
            infoText={question.description}
            name={question.guid}
            multiselect={question.type.endsWith('MULTI_SELECT')}
            visible={question.choices.filter(answers.isVisible)}
            context={answers.get(question)}
            availability={answers.getAvailability(question)}
          />
        );
      case OFQuestionType.BA_SELECTION:
        return (
          <OpenFormQuestionBillingAccount
            disabled={disabled}
            required={question.isMandatory}
            label={question.label}
            infoText={question.description}
            name={question.guid}
            context={answers.get(question)}
          />
        );
      case OFQuestionType.DATE_OF_DELIVERY:
        return (
          <OpenFormQuestionDate
            disabled={disabled}
            required={question.isMandatory}
            label={question.label}
            name={question.guid}
            offset={question.offset}
            choices={answers.get(question)?.choices}
          />
        );
      case OFQuestionType.INSTALLATION_ADDRESS:
        return <OpenFormNotice text="Use AVAILABILITY_CHECK instead of INSTALLATION_ADDRESS" type="warning" />;
      case OFQuestionType.INSTALLATION_CONTACT:
      case OFQuestionType.DELIVERY_CONTACT:
      case OFQuestionType.FAULT_INCIDENT_CONTACT:
      case OFQuestionType.ORDERING_CONTACT:
      case OFQuestionType.TECHNICAL_CONTACT:
        return (
          <OpenFormQuestionContact
            disabled={disabled}
            required={question.isMandatory}
            label={question.label}
            infoText={question.description}
            name={question.guid}
            choices={answers.get(question)?.choices}
          />
        );
      case OFQuestionType.SUBSCRIPTION_REFERENCE_ID:
        return (
          <OpenFormQuestionSubscriptionReferences
            disabled={disabled}
            required={question.isMandatory}
            label={question.label}
            infoText={question.description}
            name={question.guid}
            references={answers.get(question)?.subscriptionReferences}
          />
        );
      case OFQuestionType.ADDITIONAL_INFORMATION_BACKOFFICE:
      case OFQuestionType.ADDITIONAL_INFORMATION_FIELD_ENGINEER:
        return (
          <OpenFormQuestionInput
            disabled={disabled}
            required={question.isMandatory}
            label={question.label}
            infoText={question.description}
            name={question.guid}
            maxLength={question.maxLength}
            type="textarea"
          />
        );
      case OFQuestionType.TIME_OF_DELIVERY:
        return (
          <OpenFormQuestionInput
            disabled={disabled}
            required={question.isMandatory}
            label={question.label}
            infoText={question.description}
            name={question.guid}
            type="time"
          />
        );
      case OFQuestionType.FREE_TEXT:
        switch (question.dataType) {
          case OFQuestionDataType.DATE_FIELD:
            return (
              <OpenFormQuestionDate
                disabled={disabled}
                required={question.isMandatory}
                label={question.label}
                name={question.guid}
                offset={question.offset}
                choices={answers.get(question)?.choices}
              />
            );
          case OFQuestionDataType.EMAIL:
            return (
              <OpenFormQuestionInput
                disabled={disabled}
                required={question.isMandatory}
                label={question.label}
                infoText={question.description}
                name={question.guid}
                type="email"
              />
            );
          case OFQuestionDataType.NUMBER_CURRENCY:
            return (
              <OpenFormQuestionInput
                disabled={disabled}
                required={question.isMandatory}
                label={question.label}
                infoText={question.description}
                name={question.guid}
                type="currency"
              />
            );
          case OFQuestionDataType.NUMBER_INTEGER:
            return (
              <OpenFormQuestionInput
                disabled={disabled}
                required={question.isMandatory}
                label={question.label}
                infoText={question.description}
                name={question.guid}
                type="int"
              />
            );
          case OFQuestionDataType.PHONE:
            return (
              <OpenFormQuestionInput
                disabled={disabled}
                required={question.isMandatory}
                label={question.label}
                infoText={question.description}
                name={question.guid}
                type="tel"
              />
            );
          case OFQuestionDataType.TIME_FIELD:
            return (
              <OpenFormQuestionInput
                disabled={disabled}
                required={question.isMandatory}
                label={question.label}
                infoText={question.description}
                name={question.guid}
                type="time"
              />
            );
          case OFQuestionDataType.TEXT:
            return (
              <OpenFormQuestionInput
                disabled={disabled}
                required={question.isMandatory}
                label={question.label}
                infoText={question.description}
                name={question.guid}
                maxLength={question.maxLength}
                type="text"
              />
            );
          default:
            return null;
        }
      default:
        return null;
    }
  } catch (error) {
    return <OpenFormNotice text={error} type="error" />;
  }
};
